// Small shared formatters for the Patient Health Record (Phase 3).
//
// Kept out of the components because the tab, the card and the add form all
// print the same three things — a date, a "Recorded by …" line and a details
// list — and three copies of "which date field wins" is how two screens end up
// disagreeing about when something happened.

import { format } from 'date-fns';

/**
 * A health entry's dates are DATE-ONLY strings ('2026-09-12'); its timestamps
 * are full ISO. `new Date('2026-09-12')` is parsed as UTC midnight, which
 * prints as the 11th for anybody west of Greenwich — so a date-only value is
 * split by hand and built in LOCAL time. A visit on the 12th must never read
 * as the 11th because of where the browser is.
 */
export const toDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** "12 Sep 2026", or '' when there is no usable date. */
export const formatDay = (value) => {
    const d = toDate(value);
    return d ? format(d, 'dd MMM yyyy') : '';
};

/**
 * The date a card leads with: when the event happened, falling back to when it
 * was written down. An undated entry is real — "I have been allergic to
 * penicillin for as long as I can remember" has no date — so this may be ''.
 */
export const entryDay = (entry) => formatDay(entry?.occurredAt || entry?.created_at || null);

/**
 * The footer line, in the two shapes the contract names:
 *   "Recorded by you · 12 Sep 2026"
 *   "Recorded by Dr Meena · Chitra Clinic · 10 Sep 2026"
 * The hospital is printed only when the server resolved a NAME for it; a bare
 * tenant id (TN00001) on a patient-facing line says nothing to anyone.
 */
export const recordedByLine = (entry, t) => {
    const who = entry?.recordedByName || '';
    const date = formatDay(entry?.created_at || entry?.occurredAt || null);
    const hospital = entry?.recordedAtTenantName && entry.recordedAtTenantName !== entry.recordedAtTenantId
        ? entry.recordedAtTenantName
        : null;
    return hospital
        ? t('healthRecord.recorded_by_at', { who, hospital, date })
        : t('healthRecord.recorded_by', { who, date });
};

/**
 * The `details` object as label/value pairs, in the order the category's form
 * asks for them, with anything else the server holds appended. Nothing is
 * dropped: an entry written by the patient app, or by a later version of this
 * form, still shows everything it carries.
 */
export const detailLines = (entry, fieldsForCategory, t) => {
    const details = entry?.details && typeof entry.details === 'object' ? entry.details : {};
    const known = (fieldsForCategory || []).map((f) => f.name);
    const order = [...known, ...Object.keys(details).filter((k) => !known.includes(k))];
    return order
        .filter((k) => details[k] !== undefined && details[k] !== null && String(details[k]).trim() !== '')
        .map((k) => ({
            key: k,
            label: t(`healthRecord.details.${k}`, k),
            // Severity is a closed list, so it is translated; everything else
            // is whatever was typed.
            value: k === 'severity'
                ? t(`healthRecord.severity.${details[k]}`, String(details[k]))
                : String(details[k]),
        }));
};

/** 'donation' → "Donation". Free-text subtypes (a surgery's) print as typed. */
export const subtypeLabel = (subtype, t) =>
    (subtype ? t(`healthRecord.subtypes.${subtype}`, subtype) : '');
